/**
 * Storage persistence and quota telemetry module.
 */

/**
 * Result structure of storage quota inspection.
 */
export interface StorageEstimateResult {
  usageBytes: number;
  quotaBytes: number;
  percentUsed: number;
  persisted: boolean;
}

/**
 * Requests persistent storage from the browser to safeguard IndexedDB from eviction.
 *
 * Why: Monitoring sessions contain critical field telemetry collected over hours.
 * Requesting persistence prevents mobile browsers from purging IndexedDB when storage is tight.
 *
 * @returns Promise resolving to true if persistence was granted or already active
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.persist) {
    try {
      const isPersisted = await navigator.storage.persisted();
      if (!isPersisted) {
        return await navigator.storage.persist();
      }
      return isPersisted;
    } catch (err) {
      console.warn('[NodeRadar] Persistent storage request failed:', err);
      return false;
    }
  }
  return false;
}

/**
 * Checks current storage quota and usage.
 *
 * Why: Allows operators to monitor database capacity before extensive operations.
 *
 * @returns Promise resolving to storage quota telemetry or null if unsupported
 */
export async function getStorageEstimate(): Promise<StorageEstimateResult | null> {
  if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.estimate) {
    try {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percent = quota > 0 ? (usage / quota) * 100 : 0;
      const persisted = navigator.storage.persisted ? await navigator.storage.persisted() : false;

      return {
        usageBytes: usage,
        quotaBytes: quota,
        percentUsed: Math.round(percent * 10) / 10,
        persisted
      };
    } catch (err) {
      console.warn('[NodeRadar] Storage estimate lookup failed:', err);
      return null;
    }
  }
  return null;
}

/**
 * Formats byte values into human-readable binary units (KB, MB, GB).
 *
 * Why: Presents storage telemetry in clear units for the operator in the HUD.
 *
 * @param bytes - Size in bytes
 * @returns Formatted human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}
